import { useEffect, useState, useCallback } from 'react';
import { useAuth } from '../App';
import { Navigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import axios from 'axios';
import {
  Users, Crown, DollarSign, TrendingUp, FileText, Zap,
  Mail, AlertTriangle, ShieldCheck, Loader2,
} from 'lucide-react';

import { MissedFieldsReports } from '../components/MissedFieldsReports';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Admin() {
  const { user, loading: authLoading } = useAuth();
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [forbidden, setForbidden] = useState(false);

  const fetchAll = useCallback(async () => {
    try {
      const [s, u, m] = await Promise.all([
        axios.get(`${API}/admin/stats`, { withCredentials: true }),
        axios.get(`${API}/admin/users?limit=30`, { withCredentials: true }),
        axios.get(`${API}/admin/messages?limit=50`, { withCredentials: true }),
      ]);
      setStats(s.data);
      setUsers(u.data);
      setMessages(m.data);
    } catch (e) {
      if (e.response?.status === 403) {
        setForbidden(true);
      } else if (e.response?.status === 401) {
        setForbidden(true);
      } else {
        toast.error('Failed to load admin data');
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!authLoading && user) fetchAll();
  }, [authLoading, user, fetchAll]);

  const markRead = async (id) => {
    try {
      await axios.post(`${API}/admin/messages/${id}/read`, {}, { withCredentials: true });
      fetchAll();
    } catch (e) { toast.error('Failed'); }
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!user) return <Navigate to="/" replace />;

  if (forbidden) {
    return (
      <div className="container mx-auto px-4 md:px-8 py-16 max-w-md text-center" data-testid="admin-forbidden">
        <ShieldCheck className="w-10 h-10 mx-auto text-muted-foreground mb-4" />
        <h1 className="font-heading text-2xl mb-2">ADMIN ONLY</h1>
        <p className="text-sm text-muted-foreground">
          You don&apos;t have access to this page.
        </p>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 md:px-8 py-8 max-w-6xl" data-testid="admin-page">
      <div className="flex items-end justify-between mb-8">
        <div>
          <h1 className="font-heading text-3xl md:text-4xl mb-1">ADMIN</h1>
          <p className="text-xs uppercase tracking-widest text-muted-foreground">
            Internal dashboard · {user.email}
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={fetchAll} className="uppercase text-xs tracking-wider">
          Refresh
        </Button>
      </div>

      {/* Top stats grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-border border border-border mb-8">
        <Stat label="MRR" value={`$${(stats?.mrr_usd || 0).toFixed(2)}`} icon={<DollarSign className="w-4 h-4" />} accent />
        <Stat label="PRO USERS" value={stats?.pro_users || 0} icon={<Crown className="w-4 h-4" />} accent />
        <Stat label="TOTAL USERS" value={stats?.total_users || 0} icon={<Users className="w-4 h-4" />} />
        <Stat label="FREE USERS" value={stats?.free_users || 0} icon={<Users className="w-4 h-4" />} />
        <Stat label="SIGNUPS 24H" value={stats?.signups_24h || 0} icon={<TrendingUp className="w-4 h-4" />} />
        <Stat label="SIGNUPS 7D" value={stats?.signups_7d || 0} icon={<TrendingUp className="w-4 h-4" />} />
        <Stat label="SIGNUPS 30D" value={stats?.signups_30d || 0} icon={<TrendingUp className="w-4 h-4" />} />
        <Stat label="CANCELS 7D" value={stats?.cancellations_7d || 0} icon={<AlertTriangle className="w-4 h-4" />} />
        <Stat label="PARSES 7D" value={stats?.parses_7d || 0} icon={<FileText className="w-4 h-4" />} />
        <Stat label="AUTOFILLS 7D" value={stats?.autofills_7d || 0} icon={<Zap className="w-4 h-4" />} />
        <Stat label="LLM SPEND 30D" value={`$${(stats?.estimated_llm_spend_30d_usd || 0).toFixed(2)}`} icon={<DollarSign className="w-4 h-4" />} />
        <Stat label="UNREAD MSGS" value={stats?.unread_messages || 0} icon={<Mail className="w-4 h-4" />} alert={stats?.unread_messages > 0} />
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        {/* Recent users */}
        <section className="border border-border" data-testid="admin-users-table">
          <div className="px-5 py-3 border-b border-border bg-secondary/30">
            <h2 className="font-heading text-sm uppercase tracking-widest">RECENT USERS</h2>
          </div>
          <div className="overflow-x-auto max-h-[480px] overflow-y-auto">
            <table className="w-full text-xs">
              <thead className="sticky top-0 bg-background border-b border-border">
                <tr className="text-left text-[10px] uppercase tracking-widest text-muted-foreground">
                  <th className="px-4 py-2">EMAIL</th>
                  <th className="px-4 py-2">TIER</th>
                  <th className="px-4 py-2">CREATED</th>
                </tr>
              </thead>
              <tbody>
                {users.length === 0 && (
                  <tr><td colSpan={3} className="px-4 py-6 text-center text-muted-foreground">No users yet</td></tr>
                )}
                {users.map((u) => (
                  <tr key={u.user_id} className="border-b border-border/60 hover:bg-secondary/20">
                    <td className="px-4 py-2 font-mono truncate max-w-[220px]" title={u.email}>
                      {u.is_deleted ? <s className="text-muted-foreground">{u.email}</s> : u.email}
                    </td>
                    <td className="px-4 py-2">
                      <span className={u.subscription_tier === 'pro' ? 'text-accent uppercase font-bold' : 'uppercase text-muted-foreground'}>
                        {u.subscription_tier}
                      </span>
                    </td>
                    <td className="px-4 py-2 text-muted-foreground">{new Date(u.created_at).toLocaleDateString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        {/* Contact messages */}
        <section className="border border-border" data-testid="admin-messages-list">
          <div className="px-5 py-3 border-b border-border bg-secondary/30 flex items-center justify-between">
            <h2 className="font-heading text-sm uppercase tracking-widest">CONTACT INBOX</h2>
            <span className="text-[10px] uppercase tracking-widest text-muted-foreground">
              {messages.filter((m) => !m.is_read).length} unread
            </span>
          </div>
          <div className="max-h-[480px] overflow-y-auto">
            {messages.length === 0 ? (
              <div className="p-8 text-center text-xs uppercase tracking-widest text-muted-foreground">
                No messages
              </div>
            ) : (
              messages.map((m) => (
                <div key={m.message_id} className={`px-5 py-3 border-b border-border/60 ${!m.is_read ? 'bg-secondary/30' : ''}`}>
                  <div className="flex items-center justify-between gap-2 mb-1">
                    <span className="font-mono text-xs truncate">{m.email}</span>
                    <span className="text-[10px] uppercase tracking-widest text-muted-foreground flex-shrink-0">
                      {new Date(m.created_at).toLocaleDateString()}
                    </span>
                  </div>
                  {m.subject && <div className="text-sm font-medium mb-1">{m.subject}</div>}
                  <p className="text-xs text-muted-foreground whitespace-pre-wrap break-words">{m.message}</p>
                  {!m.is_read && (
                    <button
                      onClick={() => markRead(m.message_id)}
                      className="mt-2 text-[10px] uppercase tracking-widest text-primary hover:underline"
                      data-testid={`mark-read-${m.message_id}`}
                    >
                      Mark as read
                    </button>
                  )}
                </div>
              ))
            )}
          </div>
        </section>
      </div>

      <MissedFieldsReports />
    </div>
  );
}

function Stat({ label, value, icon, accent, alert }) {
  return (
    <div className={`bg-background p-4 ${alert ? 'bg-yellow-50 dark:bg-yellow-950/30' : ''}`}>
      <div className="flex items-center justify-between text-muted-foreground mb-2">
        <span className="text-[9px] uppercase tracking-widest">{label}</span>
        {icon}
      </div>
      <div className={`font-heading text-2xl leading-none ${accent ? 'text-accent' : ''}`}>{value}</div>
    </div>
  );
}
